import React, { useState, useCallback } from 'react';
import PlaybookStatusBadge from './PlaybookStatusBadge';
import { playbookApi } from '../api/playbookApi';
import type { SimulationResult, PlaybookStatusCategory } from '../types/playbook.types';

interface PlaybookSimulationViewProps {
  playbookId: string;
}

const PlaybookSimulationView: React.FC<PlaybookSimulationViewProps> = ({ playbookId }) => {
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedPath, setExpandedPath] = useState<number>(0);

  const runSimulation = useCallback(async (allPaths = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await playbookApi.simulatePlaybook(playbookId, { allPaths });
      setResult(res as any);
    } catch (err) {
      console.error('[Simulation] Failed:', err);
      setError(err instanceof Error ? err.message : 'Simulation failed');
    } finally {
      setLoading(false);
    }
  }, [playbookId]);

  if (!result && !loading) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
          Run a test simulation to validate the playbook structure.
        </p>
        <div className="flex justify-center gap-2">
          <button
            type="button"
            onClick={() => runSimulation(false)}
            className="rounded-md bg-lex-accent px-4 py-2 text-sm font-medium text-lex-accent-contrast hover:bg-lex-accent-hover"
          >
            Test Primary Path
          </button>
          <button
            type="button"
            onClick={() => runSimulation(true)}
            className="rounded-md border border-lex-accent px-4 py-2 text-sm font-medium text-lex-accent hover:bg-lex-state-hover dark:border-lex-accent"
          >
            Test All Paths
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-lex-accent border-t-transparent" />
        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Running simulation...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
        {error}
        <button type="button" onClick={() => runSimulation(false)} className="ml-2 underline">Retry</button>
      </div>
    );
  }

  if (!result) return null;

  const currentPath = result.paths[expandedPath];

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center gap-4 text-xs">
        <span className="text-gray-500 dark:text-gray-400">
          {result.reachableStatuses}/{result.totalStatuses} statuses reachable
        </span>
        <span className="text-gray-500 dark:text-gray-400">
          {result.paths.length} path{result.paths.length !== 1 ? 's' : ''} found
        </span>
        <button
          type="button"
          onClick={() => runSimulation(true)}
          className="text-lex-accent hover:text-lex-accent-hover"
        >
          Re-run
        </button>
      </div>

      {/* Warnings */}
      {result.warnings?.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
          <h4 className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1">Warnings</h4>
          {result.warnings.map((w, i) => (
            <div key={i} className="text-[10px] text-amber-700 dark:text-amber-300">{w}</div>
          ))}
        </div>
      )}

      {/* Dead-end warnings */}
      {result.deadEndStatuses.length > 0 && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 dark:border-rose-800 dark:bg-rose-900/20">
          <h4 className="text-xs font-semibold text-rose-700 dark:text-rose-300 mb-1">Dead-End Statuses</h4>
          <div className="flex flex-wrap gap-1">
            {result.deadEndStatuses.map((s) => (
              <span key={s.slug} className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-medium text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                {s.name}
              </span>
            ))}
          </div>
          <p className="mt-1 text-[10px] text-rose-600 dark:text-rose-400">
            These statuses have no outgoing transitions. Instances will get stuck here.
          </p>
        </div>
      )}

      {/* Orphan transitions */}
      {result.orphanTransitions.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
          <h4 className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1">Orphan Transitions</h4>
          {result.orphanTransitions.map((t) => (
            <div key={t.slug} className="text-[10px] text-amber-700 dark:text-amber-300">
              {t.name}: {t.reason}
            </div>
          ))}
        </div>
      )}

      {/* Path tabs */}
      {result.paths.length > 1 && (
        <div className="flex gap-1">
          {result.paths.map((p, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setExpandedPath(idx)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                expandedPath === idx
                  ? 'bg-lex-accent text-lex-accent-contrast'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              Path {idx + 1}{p.reachesTerminal ? '' : ' (incomplete)'}
            </button>
          ))}
        </div>
      )}

      {/* Current path: vertical status flow */}
      {currentPath && (
        <div className="relative pl-6">
          {/* Vertical line */}
          <div className="absolute left-3 top-2 bottom-2 w-px bg-gray-200 dark:bg-gray-700" />

          {currentPath.statuses.map((s, idx) => {
            const transition = currentPath.transitions[idx];
            return (
              <div key={`${s.slug}-${idx}`} className="relative mb-4 last:mb-0">
                {/* Dot */}
                <div className={`absolute -left-3 top-1 h-2.5 w-2.5 rounded-full border-2 border-white dark:border-gray-900 ${
                  s.category === 'done' ? 'bg-emerald-500' :
                  s.category === 'in_progress' ? 'bg-lex-accent' : 'bg-gray-400'
                }`} />

                {/* Status */}
                <div className="ml-2">
                  <PlaybookStatusBadge category={s.category as PlaybookStatusCategory} name={s.name} />
                </div>

                {/* Transition details (between statuses) */}
                {transition && (
                  <div className="ml-2 mt-1.5 mb-2 pl-3 border-l-2 border-lex-accent space-y-1">
                    <span className="text-[10px] font-medium text-lex-accent">
                      → {transition.name}
                    </span>
                    {transition.guardsResult?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {transition.guardsResult.map((g, gi) => (
                          <span key={gi} className={`rounded px-1 py-0.5 text-[9px] font-medium ${
                            g.passed ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                                     : 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
                          }`}>
                            {g.guard_type}: {g.passed ? 'PASS' : 'FAIL'}
                          </span>
                        ))}
                      </div>
                    )}
                    {transition.actionPreviews?.length > 0 && (
                      <div className="space-y-0.5">
                        {transition.actionPreviews.map((a, ai) => (
                          <div key={ai} className="text-[9px] text-gray-500 dark:text-gray-400">
                            <span className="font-medium">{a.action_type}</span>
                            {Object.keys(a.preview).length > 0 && (
                              <span className="ml-1 font-mono">{JSON.stringify(a.preview).slice(0, 80)}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlaybookSimulationView;
